// domain/smart-table.ts — контракт колонок, форматов и пресетов таблицы.

import type { FilterClause, SortClause, TextOperator } from "./filter-clause";

export const SMART_TABLE_ALIGNMENT = ["left", "center", "right"] as const;
export type TSmartTableAlignment = (typeof SMART_TABLE_ALIGNMENT)[number];
export const SMART_TABLE_ALIGNMENT_OBJ = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
} as const satisfies Record<string, TSmartTableAlignment>;

export const PROCESSING_MODES = ["server", "client", "none"] as const;
export type ProcessingMode = (typeof PROCESSING_MODES)[number];
export const PROCESSING_MODE_OBJ = {
  SERVER: PROCESSING_MODES[0],
  CLIENT: PROCESSING_MODES[1],
  NONE: PROCESSING_MODES[2],
} as const satisfies Record<string, ProcessingMode>;

export const COLUMN_VALUE_FORMATS = [
  "date",
  "datetime",
  "boolean",
  "percent",
  "number",
  "currency",
  "array",
  "id",
  "chip",
  "text",
] as const;
export type ColumnValueFormat = (typeof COLUMN_VALUE_FORMATS)[number];
export const COLUMN_VALUE_FORMAT_OBJ = {
  DATE: COLUMN_VALUE_FORMATS[0],
  DATETIME: COLUMN_VALUE_FORMATS[1],
  BOOLEAN: COLUMN_VALUE_FORMATS[2],
  PERCENT: COLUMN_VALUE_FORMATS[3],
  NUMBER: COLUMN_VALUE_FORMATS[4],
  CURRENCY: COLUMN_VALUE_FORMATS[5],
  ARRAY: COLUMN_VALUE_FORMATS[6],
  ID: COLUMN_VALUE_FORMATS[7],
  CHIP: COLUMN_VALUE_FORMATS[8],
  TEXT: COLUMN_VALUE_FORMATS[9],
} as const satisfies Record<string, ColumnValueFormat>;

export const CHIP_VARIANTS = [
  "default",
  "secondary",
  "destructive",
  "emerald",
  "outline",
] as const;
export type ChipVariant = (typeof CHIP_VARIANTS)[number];

export interface ColumnFormatOptions {
  prefix?: string;
  suffix?: string;
  decimals?: number;
  separator?: string;
  truncate?: number;
  variants?: Record<string, ChipVariant>;
}

export interface SmartTableColumnMeta {
  align?: TSmartTableAlignment;
  filterable?: ProcessingMode;
  sortable?: ProcessingMode;
  filterType?: FilterClause["type"];
  selectOptions?: string[];
  multiSelect?: boolean;
  isAction?: boolean;
  editable?: boolean;
  format?: ColumnValueFormat;
  formatOptions?: ColumnFormatOptions;
  headerColor?: string;
  headerTextColor?: string;
  cellColor?: string;
  cellTextColor?: string;
  customCell?: boolean;
}

export interface ColumnFormat {
  label: Record<string, string>;
  tooltip?: Record<string, string> | undefined;
  headerColor?: string | undefined;
  headerTextColor?: string | undefined;
  cellColor?: string | undefined;
  cellTextColor?: string | undefined;
}

export interface AvailableFilter {
  propertyKey: string;
  order?: number | undefined;
  uiType: string;
  format: {
    label: Record<string, string>;
    tooltip?: Record<string, string> | undefined;
  };
  conditions: FilterClause[];
}

export interface SelectListItem {
  value: string;
  label: Record<string, string>;
}

export interface SelectList {
  propertyKey: string;
  items: SelectListItem[];
}

export interface SmartTableDefaultFilter {
  column: string;
  operator?: TextOperator | undefined;
}

export interface SmartTablePreset {
  id: string;
  name: string;
  columns: string[];
  filters: FilterClause[];
  sort: SortClause[];
  pageSize: number;
  stickyColumns?: string[] | undefined;
  hiddenColumns?: string[] | undefined;
  columnGroups?: ColumnGroup[] | undefined;
  columnMeta?: Record<string, ColumnFormat> | undefined;
  availableFilters?: AvailableFilter[] | undefined;
  lists?: SelectList[] | undefined;
  defaultFilter?: SmartTableDefaultFilter | undefined;
  version?: number | undefined;
  isSeed?: boolean | undefined;
}

export interface ColumnGroup {
  id: string;
  label: string;
  columnIds: string[];
  collapsedByDefault?: boolean | undefined;
}

export interface UserPreset extends SmartTablePreset {
  version: number;
  createdAt: string;
}

export const PRESET_VERSION = 4;
export const PRESET_STORAGE_KEY = "smart-table-presets";
